import { Booking, Product } from "../types/booking";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  getSendcloudCustomerEtaOverrideDeadlineYmd,
  todayYmdEuropeAmsterdam,
} from "./nlPublicHolidays";

dayjs.extend(utc);
dayjs.extend(timezone);

const CUSTOMER_ETA_OVERRIDE_TZ = "Europe/Amsterdam";

/**
 * Generate a formatted reference number for a booking
 */
export const getBookingReference = (booking: Booking): string => {
  if (booking.reference) return booking.reference;
  const transportAbbrev = booking.transport_type_info?.[0]?.abbreviation || '';
  return `EFF${booking.id}${transportAbbrev}`;
};

/**
 * Always `EFF{id}{transportAbbrev}` (e.g. EFF5183SEA, EFF123AIR) — ignores a
 * custom human `reference` string; same pattern as default booking refs elsewhere.
 */
export function getBookingEffReference(booking: Booking): string {
  const tt = booking.transport_type_info;
  const transportAbbrev = (
    Array.isArray(tt)
      ? tt[0]?.abbreviation
      : (tt as { abbreviation?: string } | undefined)?.abbreviation
  )
    ?.trim()
    .toUpperCase() ?? "";
  return formatBookingEffReferenceFromParts(booking.id, transportAbbrev);
}

/** EFF{id}{SEA|AIR|…} when transport abbreviation is known (e.g. from booking id lookup). */
export function formatBookingEffReferenceFromParts(
  bookingId: number,
  transportAbbrev?: string | null
): string {
  const abbr = transportAbbrev?.trim().toUpperCase() ?? "";
  return `EFF${bookingId}${abbr}`;
}

/**
 * Reference for LeanCustoms (order/declaration): when the booking has a linked
 * container: "<container reference> / EFF{bookingId}{SEA|AIR|...}"; otherwise
 * "EFF{bookingId}{SEA|AIR|...}".
 */
export const getLeanCustomsReference = (booking: Booking): string => {
  const tt = booking.transport_type_info;
  const transportAbbrev =
    (Array.isArray(tt) ? tt[0]?.abbreviation : (tt as { abbreviation?: string })?.abbreviation) || "";
  const effPart = `EFF${booking.id}${transportAbbrev}`;
  const ci = booking.container_info;
  const container = !ci
    ? null
    : Array.isArray(ci) && ci.length > 0
      ? ci[0]
      : (Array.isArray(ci) ? null : ci);
  if (container?.reference) return `${container.reference} / ${effPart}`;
  return effPart;
};

/** True when the booking has at least one commercial invoice file path stored. */
export function bookingHasCommercialInvoice(
  booking: Pick<Booking, "commercial_invoice_file">
): boolean {
  const raw = booking.commercial_invoice_file;
  if (!raw || typeof raw !== "string") return false;
  return raw.split(",").some((segment) => segment.trim().length > 0);
}

/** Minimal shape for matching Lean Customs declarations to a booking by EFF reference. */
export type LeanCustomsDeclarationRefSlice = {
  id?: string;
  reference?: string;
  booking_number?: string;
};

/** Numeric booking id from strings like EFF1234, EFF1234AIR, EFF1234SEA. */
export function extractBookingIdFromEffText(
  text: string | undefined | null
): number | null {
  if (!text) return null;
  const m = String(text).match(/EFF(\d+)/i);
  return m ? parseInt(m[1], 10) : null;
}

/** True when declaration reference or booking_number points at this booking id. */
export function leanCustomsDeclarationMatchesBooking(
  d: LeanCustomsDeclarationRefSlice,
  bookingId: number
): boolean {
  return (
    extractBookingIdFromEffText(d.reference) === bookingId ||
    extractBookingIdFromEffText(d.booking_number) === bookingId
  );
}

/** Lean Customs API may expose order as `order_id` or nested `order.id`. */
export function leanCustomsDeclarationLinkedOrderId(d: Record<string, unknown>): string | null {
  const tryId = (v: unknown): string | null => {
    if (typeof v === "string" && v.trim()) return v.trim();
    return null;
  };

  for (const key of ["order_id", "lc_order_id", "linked_order_id"] as const) {
    const t = tryId(d[key]);
    if (t) return t;
  }

  const ord = d.order;
  if (typeof ord === "string") return tryId(ord);
  if (ord && typeof ord === "object" && ord !== null && "id" in ord) {
    const t = tryId((ord as { id: unknown }).id);
    if (t) return t;
  }

  const shipments = d.shipments;
  if (Array.isArray(shipments) && shipments[0] && typeof shipments[0] === "object") {
    const s0 = shipments[0] as Record<string, unknown>;
    const t = tryId(s0.order_id) ?? tryId(s0["order_id"]);
    if (t) return t;
  }

  return null;
}

/** Minimal shape for matching Lean Customs orders to a booking by EFF reference. */
export type LeanCustomsOrderRefSlice = {
  id?: string;
  reference?: string;
  booking_number?: string;
};

export function leanCustomsOrderMatchesBooking(
  o: LeanCustomsOrderRefSlice,
  bookingId: number
): boolean {
  return (
    extractBookingIdFromEffText(o.reference) === bookingId ||
    extractBookingIdFromEffText(o.booking_number) === bookingId
  );
}

/**
 * Generate URL for packing slip
 */
export const generatePackingSlipUrl = (booking: Booking): string | null => {
  if (!booking || !booking.product_info || !booking.company_info || 
      !booking.supplier_info || !booking.booking_delivery_info) {
    return null;
  }

  // Handle different types of data structures safely
  const products = Array.isArray(booking.product_info) && booking.product_info.length > 0
    ? Array.isArray(booking.product_info[0]) 
      ? booking.product_info[0] 
      : booking.product_info
    : [];
    
  const productArray = Array.isArray(products) ? products : [];
    
  const company = Array.isArray(booking.company_info) && booking.company_info.length > 0
    ? booking.company_info[0]
    : booking.company_info;
    
  const supplier = Array.isArray(booking.supplier_info) && booking.supplier_info.length > 0
    ? booking.supplier_info[0]
    : booking.supplier_info;
    
  const deliveryInfo = Array.isArray(booking.booking_delivery_info) && booking.booking_delivery_info.length > 0
    ? booking.booking_delivery_info[0]
    : booking.booking_delivery_info;
    
  const transportTypeInfo = Array.isArray(booking.transport_type_info) && booking.transport_type_info.length > 0
    ? booking.transport_type_info[0]
    : booking.transport_type_info;
    
  const transportType = transportTypeInfo?.abbreviation || '';

  const url = new URL("https://app.efreightforwarding.com/regenerate-packingslip");
  
  // Add query parameters
  const params: Record<string, string> = {
    date: new Date().toLocaleDateString('en-GB'),
    reference: `EFF${booking.id}${transportType}`,
    bookingId: booking.id.toString(),
    companyId: (booking.company_id || '').toString(),
    
    // Ship To information
    shipToCompany: encodeURIComponent(company?.name || ''),
    shipToAddress: encodeURIComponent(company?.address || ''),
    shipToCity: encodeURIComponent(company?.city || ''),
    shipToPostalCode: company?.postal_code || '',
    shipToCountry: company?.country || '',
    shipToEoriNumber: company?.eori_number || '',
    shipToCocNumber: company?.coc_number?.toString() || '',
    shipToStreet: encodeURIComponent(deliveryInfo?.street_name || ''),
    shipToHouseNumber: deliveryInfo?.house_number?.toString() || '',
    
    // Product information
    productIds: productArray.map((p) => p.ean || '').join(','),
    descriptions: productArray.map((p) => encodeURIComponent(p.name || '')).join(','),
    pieces: productArray.map((p) => p.pieces?.toString() || '0').join(','),
    cartons: productArray.map((p) => p.cartons?.toString() || '0').join(','),
    weights: productArray.map((p) => p.gross_weight.toString()).join(','),
    cbms: productArray.map((p) => p.cbm.toString()).join(','),
    netWeights: productArray.map((p) => ((p.gross_weight || 0) * 0.9).toFixed(2)).join(','),
    hsCodes: productArray.map((p) => p.hs_code || '').join(','),
    
    // Supplier information
    supplierInfoId: supplier?.id?.toString() || '',
    supplierInfoName: encodeURIComponent(supplier?.name || ''),
    supplierInfoEmail: encodeURIComponent(supplier?.email || ''),
    supplierInfoPhone: encodeURIComponent(supplier?.phone || ''),
    supplierInfoAddress: encodeURIComponent(supplier?.address || ''),
    supplierInfoCountry: supplier?.country || '',
    supplierInfoProvince: supplier?.province || ''
  };

  // Add all parameters to URL
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value);
  });

  return url.toString();
};

/**
 * Get status color based on booking status
 */
export const getStatusColor = (status: string | null): string => {
  if (!status) return "default";
  
  switch (status) {
    case "waiting_for_goods":
      return "blue";
    case "cancelled":
      return "red";
    case "out_for_delivery":
      return "cyan";
    case "concept":
      return "purple";
    case "added_to_stock":
      return "green";
    case "goods_received_origin":
      return "geekblue";
    case "approved":
      return "success";
    case "in_transit":
      return "processing";
    case "customs_pending_clearance":
      return "orange";
    case "customs_cleared":
      return "success";
    case "fyco":
      return "processing";
    case "archived":
      return "gray";
    case "rejected":
      return "error";
    case "delivered":
      return "green";
    case "submitted":
      return "processing";
    case "in_progress":
      return "warning";
    case "completed":
      return "success";
    default:
      return "default";
  }
};

/**
 * Format date for display
 */
export const formatDate = (dateString: string | null): string => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString();
};

/**
 * Format datetime for display
 */
export const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleString();
};

/**
 * `bookings.eta` is the per-booking customer ETA override. Block Sendcloud (LMD)
 * labels when that date is strictly after the holiday-aware deadline
 * (Europe/Amsterdam civil dates): calendar tomorrow, extended when tomorrow or
 * the following days are weekends or Dutch public holidays so short weeks still
 * allow labels (e.g. Hemelvaart donderdag → vrijdag override).
 */
export function isCustomerEtaOverrideBlockingSendcloudLabels(
  etaOverride: string | null | undefined
): boolean {
  if (etaOverride == null || String(etaOverride).trim() === "") {
    return false;
  }
  const etaDay = dayjs(etaOverride).tz(CUSTOMER_ETA_OVERRIDE_TZ).startOf("day");
  if (!etaDay.isValid()) {
    return false;
  }
  const deadlineYmd = getSendcloudCustomerEtaOverrideDeadlineYmd(
    todayYmdEuropeAmsterdam()
  );
  const etaYmd = etaDay.format("YYYY-MM-DD");
  return etaYmd > deadlineYmd;
}

/**
 * True when a booking-level customer ETA (`bookings.eta`) is set and differs from
 * the linked container's `eta_customer` (same calendar day in Amsterdam), or when
 * the container has no customer ETA but the booking override is set.
 */
export function isMeaningfulCustomerEtaOverride(
  bookingEta: string | null | undefined,
  containerEtaCustomer: string | null | undefined
): boolean {
  if (bookingEta == null || String(bookingEta).trim() === "") {
    return false;
  }
  const overrideYmd = dayjs(bookingEta)
    .tz(CUSTOMER_ETA_OVERRIDE_TZ)
    .format("YYYY-MM-DD");
  if (
    containerEtaCustomer == null ||
    String(containerEtaCustomer).trim() === ""
  ) {
    return true;
  }
  const containerYmd = dayjs(containerEtaCustomer)
    .tz(CUSTOMER_ETA_OVERRIDE_TZ)
    .format("YYYY-MM-DD");
  return overrideYmd !== containerYmd;
} 